import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  Message,
  Loader,
  InputGroup,
  Input,
  Pagination,
  SelectPicker,
  Tag,
  Modal,
  Button,
} from "rsuite";
import TrashIcon from "@rsuite/icons/Trash";
import EditIcon from "@rsuite/icons/Edit";
import { queryPostRequest } from "../../api";
import "./FeedbackTablePage.css";

const { Column, HeaderCell, Cell } = Table;

const safeArray = (value) => (Array.isArray(value) ? value : []);

const formatDateTime = (ts) => {
  if (!ts) return "";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return String(ts);
  return d.toLocaleString();
};

const normalizeId = (row) => {
  if (row?.id) return String(row.id);

  if (row?._id && typeof row._id === "string") return row._id;
  if (row?._id?.$oid) return String(row._id.$oid);
  if (row?._id?.oid) return String(row._id.oid);

  if (row?._id && typeof row._id === "object") {
    if (row._id.id) return String(row._id.id);
    try {
      const asString = String(row._id);
      if (asString && asString !== "[object Object]") return asString;
    } catch {
      // ignore
    }
  }

  return "";
};

const normalizeRows = (rows) =>
  safeArray(rows).map((row) => {
    const fallbackTimestamp = row?._id?.date;
    return {
      ...row,
      id: normalizeId(row),
      timestamp: row?.timestamp || fallbackTimestamp,
    };
  });

const getTypeColor = (type) => {
  const t = String(type || "")
    .trim()
    .toLowerCase();
  if (t === "bug") return "red";
  if (t === "feature request" || t === "feature") return "blue";
  if (t === "comments" || t === "comment") return "green";
  return "blue";
};

const toSortableTime = (ts) => {
  if (!ts) return 0;
  const raw = String(ts).trim();

  const direct = new Date(raw);
  const directMs = direct.getTime();
  if (!Number.isNaN(directMs)) return directMs;
  const noComma = raw.replace(/,\s*/, " ");
  const d2 = new Date(noComma);
  const ms2 = d2.getTime();
  if (!Number.isNaN(ms2)) return ms2;

  return 0;
};

const canonicalType = (type) => {
  const t = String(type || "")
    .trim()
    .toLowerCase();
  if (t === "bug") return "Bug";
  if (t === "feature request" || t === "feature") return "Feature request";
  if (t === "comments" || t === "comment") return "Comments";
  return type || "";
};

const FeedbackTablePage = () => {
  const [feedback, setFeedback] = useState([]);
  const [jiraLinks, setJiraLinks] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("All");
  const [sortColumn, setSortColumn] = useState("timestamp");
  const [sortType, setSortType] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const [editJiraModalOpen, setEditJiraModalOpen] = useState(false);
  const [editJiraRowId, setEditJiraRowId] = useState(null);
  const [editJiraValue, setEditJiraValue] = useState("");
  const [editJiraLoading, setEditJiraLoading] = useState(false);
  const [editJiraError, setEditJiraError] = useState(null);

  const fetchFeedback = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await queryPostRequest("get_feedback", {});

      const rows =
        (data && Array.isArray(data.items) && data.items) ||
        (data && Array.isArray(data.feedback) && data.feedback) ||
        (data && Array.isArray(data.data) && data.data) ||
        (Array.isArray(data) ? data : []);

      const normalized = normalizeRows(rows);

      setFeedback(normalized);
    } catch (e) {
      setFeedback([]);
      setError(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let isCancelled = false;

    const run = async () => {
      await fetchFeedback();
    };

    run();

    return () => {
      isCancelled = true;
    };
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, typeFilter, rowsPerPage, feedback.length]);

  const filteredFeedback = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();

    return safeArray(feedback).filter((row) => {
      const rowType = canonicalType(row?.type);
      const matchesType = typeFilter === "All" ? true : rowType === typeFilter;

      if (!matchesType) return false;

      if (!q) return true;

      const haystack = [row?.timestamp, row?.name, rowType, row?.feedback]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(q);
    });
  }, [feedback, searchQuery, typeFilter]);

  const sortedFeedback = useMemo(() => {
    const data = safeArray(filteredFeedback);

    if (sortColumn !== "timestamp") return data;

    const direction = sortType === "asc" ? 1 : -1;
    return [...data].sort((a, b) => {
      const ta = toSortableTime(a?.timestamp);
      const tb = toSortableTime(b?.timestamp);
      return (ta - tb) * direction;
    });
  }, [filteredFeedback, sortColumn, sortType]);

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return safeArray(sortedFeedback).slice(start, start + rowsPerPage);
  }, [sortedFeedback, currentPage, rowsPerPage]);

  const openDeleteModal = (row) => {
    setDeleteError(null);
    setDeleteTarget(row);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    if (deleteLoading) return;
    setDeleteModalOpen(false);
    setDeleteTarget(null);
    setDeleteError(null);
  };

  const confirmDelete = async () => {
    const id = deleteTarget?.id;
    if (!id) {
      setDeleteError(new Error("Missing feedback id"));
      return;
    }

    setDeleteLoading(true);
    setDeleteError(null);

    try {
      await queryPostRequest("delete_feedback", { id });

      setFeedback((prev) => safeArray(prev).filter((r) => r?.id !== id));
      closeDeleteModal();

      await fetchFeedback();
    } catch (e) {
      setDeleteError(e);
    } finally {
      setDeleteLoading(false);
    }
  };

  const openEditJiraModal = (rowId, currentValue) => {
    setEditJiraRowId(rowId);
    setEditJiraValue(currentValue || "");
    setEditJiraModalOpen(true);
    setEditJiraError(null);
  };
  const closeEditJiraModal = () => {
    if (editJiraLoading) return;
    setEditJiraModalOpen(false);
    setEditJiraRowId(null);
    setEditJiraValue("");
    setEditJiraError(null);
  };
  const saveEditJira = async () => {
    if (!editJiraRowId) return;
    setEditJiraLoading(true);
    setEditJiraError(null);
    try {
      await queryPostRequest("update_jira_link", { id: editJiraRowId, jira: editJiraValue });
      setJiraLinks(prev => ({ ...prev, [editJiraRowId]: editJiraValue }));
      closeEditJiraModal();
      await fetchFeedback();
    } catch (e) {
      setEditJiraError(e);
    } finally {
      setEditJiraLoading(false);
    }
  };

  return (
    <div className="feedback-table-page" data-testid="feedback-table-page">
      <div className="feedback-header">
        <h4 className="feedback-title">Feedback</h4>

        <div className="feedback-header-controls">
          <SelectPicker
            className="feedback-type-dropdown"
            cleanable={false}
            searchable={false}
            placement="bottomEnd"
            appearance="default"
            data={[
              { label: "All", value: "All" },
              { label: "Bug", value: "Bug" },
              { label: "Feature request", value: "Feature request" },
              { label: "Comments", value: "Comments" },
            ]}
            value={typeFilter}
            onChange={(value) => setTypeFilter(value || "All")}
            style={{ width: 170 }}
            placeholder="Type"
          />

          <InputGroup className="feedback-search">
            <Input
              placeholder="Search"
              value={searchQuery}
              onChange={(value) => setSearchQuery(value)}
              autoComplete="off"
            />
          </InputGroup>
        </div>
      </div>

      {loading ? (
        <div className="feedback-loading">
          <Loader center />
        </div>
      ) : error ? (
        <Message showIcon type="error">
          Failed to load feedback
        </Message>
      ) : (
        <>
          <div className="feedback-table-container">
            <Table
              data={paginatedData}
              height={600}
              className="feedback-table"
              wordWrap
              bordered
              cellBordered
              sortColumn={sortColumn}
              sortType={sortType}
              onSortColumn={(column, type) => {
                setSortColumn(column);
                setSortType(type);
              }}
            >
              <Column width={200} align="left" fixed sortable resizable>
                <HeaderCell>Date time</HeaderCell>
                <Cell dataKey="timestamp">
                  {(rowData) => formatDateTime(rowData?.timestamp)}
                </Cell>
              </Column>

              <Column width={280} align="left" resizable>
                <HeaderCell>Name</HeaderCell>
                <Cell dataKey="name" />
              </Column>

              <Column width={210} align="center" resizable>
                <HeaderCell>Type</HeaderCell>
                <Cell>
                  {(rowData) => {
                    const t = canonicalType(rowData?.type);
                    return <Tag color={getTypeColor(t)}>{t || ""}</Tag>;
                  }}
                </Cell>
              </Column>

              <Column flexGrow={1} align="left" resizable>
                <HeaderCell>Feedback</HeaderCell>
                <Cell dataKey="feedback" />
              </Column>

              <Column width={220} align="left" resizable>
                <HeaderCell>Jira Ticket</HeaderCell>
                <Cell>
                  {(rowData) => {
                    const rowId = rowData?.id;
                    const jiraLink = jiraLinks[rowId] ?? rowData?.jira ?? "";
                    let jiraKey = "";
                    if (jiraLink) {
                      const match = jiraLink.match(/browse\/([A-Z]+-\d+)/i);
                      jiraKey = match ? match[1] : jiraLink.split("/").pop();
                    }
                    return (
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <div>
                          {jiraLink && jiraKey ? (
                            <a
                              href={jiraLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: '#1677ff', textDecoration: 'underline', fontWeight: 500 }}
                            >
                              {jiraKey}
                            </a>
                          ) : (
                            <span style={{ color: '#aaa' }}>No Jira story</span>
                          )}
                        </div>
                        <Button
                          appearance="subtle"
                          size="xs"
                          onClick={() => openEditJiraModal(rowId, jiraLink)}
                          title="Edit Jira link"
                          style={{ marginLeft: 8 }}
                        >
                          <EditIcon />
                        </Button>
                      </div>
                    );
                  }}
                </Cell>
              </Column>

              <Column width={64} align="center" fixed="right">
                <HeaderCell />
                <Cell>
                  {(rowData) => (
                    <button
                      type="button"
                      className="feedback-delete-button"
                      title={rowData?.id ? "Delete feedback" : "Missing id"}
                      aria-label="Delete feedback"
                      onClick={() => openDeleteModal(rowData)}
                      disabled={!rowData?.id}
                    >
                      <TrashIcon />
                    </button>
                  )}
                </Cell>
              </Column>
            </Table>
          </div>

          <div className="feedback-pagination">
            <Pagination
              prev
              next
              ellipsis
              boundaryLinks
              maxButtons={5}
              size="xs"
              layout={["total", "-", "|", "pager"]}
              total={safeArray(sortedFeedback).length}
              limitOptions={[10, 20, 30, 50]}
              limit={rowsPerPage}
              activePage={currentPage}
              onChangePage={setCurrentPage}
              onChangeLimit={(value) => {
                setRowsPerPage(value);
                setCurrentPage(1);
              }}
            />
          </div>
        </>
      )}

      <Modal
        open={deleteModalOpen}
        onClose={closeDeleteModal}
        size="xs"
        role="alertdialog"
        backdrop="static"
      >
        <Modal.Header>
          <Modal.Title>Delete feedback</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this feedback?
          {deleteError ? (
            <Message showIcon type="error" style={{ marginTop: 12 }}>
              Failed to delete feedback
            </Message>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={closeDeleteModal}
            appearance="subtle"
            disabled={deleteLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            appearance="primary"
            color="red"
            loading={deleteLoading}
          >
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        open={editJiraModalOpen}
        onClose={closeEditJiraModal}
        size="lg"
        role="dialog"
        backdrop="static"
      >
        <Modal.Header style={{ width: "25rem" }}>
          <Modal.Title>Edit Jira Link</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Input
            value={editJiraValue}
            onChange={setEditJiraValue}
            placeholder="Enter Jira URL"
            autoFocus
          />
          {editJiraError ? (
            <Message showIcon type="error" style={{ marginTop: 12 }}>
              Failed to update Jira link
            </Message>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={closeEditJiraModal}
            appearance="subtle"
            disabled={editJiraLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={saveEditJira}
            appearance="primary"
            color="blue"
            loading={editJiraLoading}
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default FeedbackTablePage;
